import type { CustomDataArtifact } from '@dev-streetbeat/chat';

/**
 * Portfolio extraction utilities for SDK artifacts
 */

export interface PortfolioExtractionResult {
    portfolioData: any | null;
    portfolioId: string | null;
    commentoAlPortafoglio: boolean;
    isFinecoImportedPortfolio: boolean;
    canImportToXnet: boolean;
    isOptimizedPortfolio: boolean;
}

/**
 * Checks if an artifact is a Fineco portfolio custom_data artifact
 */
export function isFinecoPortfolioArtifact(artifact: any): artifact is CustomDataArtifact {
    return (
        artifact?.type === "custom_data" &&
        artifact.custom_data?.type === "fineco_portfolio" &&
        artifact.custom_data?.data != null
    );
}

/**
 * Extracts portfolio data from SDK CustomDataArtifact format
 */
export function getPortfolioData(artifact: any): any | null {
    if (isFinecoPortfolioArtifact(artifact)) {
        const data = artifact.custom_data.data as any;
        return data?.data || null;
    }
    return null;
}

/**
 * Extracts portfolio ID from SDK CustomDataArtifact format
 */
export function getPortfolioId(artifact: any): string | null {
    if (isFinecoPortfolioArtifact(artifact)) {
        const data = artifact.custom_data.data as any;
        return data?.portfolio_id || null;
    }
    return null;
}

/**
 * Extracts commento_al_portafoglio flag from SDK CustomDataArtifact format
 */
function getCommentoAlPortafoglio(artifact: any): boolean {
    if (isFinecoPortfolioArtifact(artifact)) {
        const data = artifact.custom_data.data as any;
        return data?.commento_al_portafoglio || false;
    }
    return false;
}

/**
 * Extracts fineco_imported_portfolio flag from SDK CustomDataArtifact format
 */
function getIsFinecoImportedPortfolio(artifact: any): boolean {
    if (isFinecoPortfolioArtifact(artifact)) {
        const data = artifact.custom_data.data as any;
        return data?.fineco_imported_portfolio || false;
    }
    return false;
}

/**
 * Extracts import_to_xnet flag from SDK CustomDataArtifact format
 */
function getCanImportToXnet(artifact: any): boolean {
    if (isFinecoPortfolioArtifact(artifact)) {
        const data = artifact.custom_data.data as any;
        return data?.import_to_xnet || false;
    }
    return false;
}

/**
 * Extracts is_optimized_portfolio flag from SDK CustomDataArtifact format
 */
function getIsOptimizedPortfolio(artifact: any): boolean {
    if (isFinecoPortfolioArtifact(artifact)) {
        const data = artifact.custom_data.data as any;
        return data?.is_optimized_portfolio || false;
    }
    return false;
}

/**
 * Extracts all portfolio-related data from an artifact in one call
 */
export function extractPortfolioFromArtifact(artifact: any): PortfolioExtractionResult {
    return {
        portfolioData: getPortfolioData(artifact),
        portfolioId: getPortfolioId(artifact),
        commentoAlPortafoglio: getCommentoAlPortafoglio(artifact),
        isFinecoImportedPortfolio: getIsFinecoImportedPortfolio(artifact),
        canImportToXnet: getCanImportToXnet(artifact),
        isOptimizedPortfolio: getIsOptimizedPortfolio(artifact),
    };
}

/**
 * Checks if an artifact is a portfolio story custom_data artifact
 */
export function isPortfolioStoryArtifact(artifact: any): artifact is CustomDataArtifact {
    return (
        artifact?.type === "custom_data" &&
        artifact.custom_data?.type === "portfolio_story" &&
        artifact.custom_data?.data != null
    );
}

/**
 * Keeps one fineco_portfolio artifact per portfolio_id: the last one. A turn whose
 * tool ran twice on the same portfolio (an agent retry) carries both results, and
 * the reply would show the same portfolio card twice.
 */
export function dedupeFinecoPortfolioArtifacts<T>(artifacts: T[]): T[] {
    const lastIndex = new Map<string, number>();
    artifacts.forEach((artifact, index) => {
        const portfolioId = getPortfolioId(artifact);
        if (portfolioId) lastIndex.set(portfolioId, index);
    });
    return artifacts.filter((artifact, index) => {
        const portfolioId = getPortfolioId(artifact);
        return !portfolioId || lastIndex.get(portfolioId) === index;
    });
}
